import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import { updateProfile } from '../containers/ManageAccountPage/ManageAccountPage.duck';
import * as log from '../util/log';
import {
  applyColorScheme,
  COLOR_SCHEME_TOPBAR_BACKGROUNDS,
  resolveColorScheme,
  resolveColorSchemePreference,
  writeStoredColorSchemePreference,
} from '../util/colorScheme';

const ColorSchemeContext = createContext(null);

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const ColorSchemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);

  const preference = resolveColorSchemePreference({ currentUser });
  const colorScheme = resolveColorScheme({ preference });

  const [resolvedScheme, setResolvedScheme] = useState(colorScheme);

  const syncScheme = useCallback(nextPreference => {
    const effectiveScheme = resolveColorScheme({ preference: nextPreference });
    applyColorScheme(effectiveScheme);
    setResolvedScheme(effectiveScheme);
  }, []);

  useEffect(() => {
    syncScheme(preference);
    writeStoredColorSchemePreference(preference);
  }, [preference, syncScheme]);

  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => syncScheme('system');

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [preference, syncScheme]);

  const setPreference = useCallback(
    nextPreference => {
      writeStoredColorSchemePreference(nextPreference);
      syncScheme(nextPreference);

      if (!currentUser) {
        return;
      }

      dispatch(
        updateProfile({
          privateData: {
            colorScheme: nextPreference,
          },
        })
      ).catch(error => {
        log.error(error, 'color-scheme-preference-update-failed');
      });
    },
    [currentUser, dispatch, syncScheme]
  );

  const value = useMemo(
    () => ({
      preference,
      colorScheme: resolvedScheme,
      setPreference,
    }),
    [preference, resolvedScheme, setPreference]
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
};

export const useColorScheme = () => {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme must be used within a ColorSchemeProvider');
  }
  return context;
};

/**
 * Sets theme-color meta for mobile browser chrome (status bar area with clock / wifi / battery).
 * Uses the topbar navy so that strip matches the Topbar. Also hints iOS standalone status bar.
 */
export const ColorSchemeHelmet = () => {
  const { colorScheme } = useColorScheme();
  const themeColor = COLOR_SCHEME_TOPBAR_BACKGROUNDS[colorScheme];

  if (!themeColor) {
    return null;
  }

  return (
    <Helmet>
      <meta name="theme-color" content={themeColor} />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
    </Helmet>
  );
};
